import React, { useState, useEffect, useRef } from "react";
import Loader from "../../Shared/Loader";

import "../../../css/HelpdeskQueryList.css";
import "./QueryResponseModal.css";
import apiClient from "../../../api/axiosConfig";
import { ITicket } from "./HelpdeskQueryList";
import { showToast } from '../../Shared/Toaster';
import CreateRefundModal from "./CreateRefundModal";
import RescheduleConfigModal from "./RescheduleConfigModal";

// Module-level cache: survives modal close/reopen within the same browser session
// Key: fileName, Value: full data-URL (data:image/...;base64,...)
const imageCache = new Map<string, string>();


interface IAttachment {
  _id?: string;
  type?: string;
  data?: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
}

interface IMessage {
  _id: string;
  senderId: string;
  messageText: string;
  sentOn: string;
  attachments?: IAttachment[];
}

interface Props {
  ticket: ITicket;
  onClose: () => void;
  onUpdate: () => void;
  onEditStatus?: () => void;
}

const QueryResponseModal: React.FC<Props> = ({ ticket, onClose, onUpdate, onEditStatus }) => {
  const [currentTicket, setCurrentTicket] = useState<ITicket>(ticket);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [userQueries, setUserQueries] = useState<ITicket[]>([]);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [pendingAttachment, setPendingAttachment] = useState<{ fileName: string; mimeType: string; data: string; file: File } | null>(null);
  const lastSentImageRef = useRef<{ fileName: string; mimeType: string; data: string } | null>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isInitialLoadRef = useRef(true);

  // Update currentTicket if parent ticket prop changes
  useEffect(() => {
    setCurrentTicket(ticket);
    isInitialLoadRef.current = true;
  }, [ticket]);

  // 🔹 1. Fetch Messages Function (Reusable for active selected ticket)
  const fetchMessages = async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    try {
      const res = await apiClient.helpdeskApi.get(
        `/api/admin/tickets/${currentTicket._id}/messages`
      );
      let allMessages: IMessage[] = res.data || [];

      if (currentTicket.message) {
        allMessages = [
          {
            _id: "initial-msg",
            senderId: currentTicket.raisedBy,
            messageText: currentTicket.message,
            sentOn: currentTicket.createdAt,
          },
          ...allMessages,
        ];
      }

      if (lastSentImageRef.current) {
        const lastMessage = allMessages[allMessages.length - 1];
        if (
          lastMessage &&
          lastMessage.senderId === "admin" &&
          (lastMessage.messageText.includes("Image:") || lastMessage.messageText.includes("📷"))
        ) {
          const normalizedText = lastMessage.messageText.toLowerCase();
          const fileName = lastSentImageRef.current.fileName.toLowerCase();
          if (!normalizedText.includes(fileName.toLowerCase().replace(/\s+/g, ""))) {
            lastMessage.attachments = [
              {
                type: "image",
                data: lastSentImageRef.current.data,
                fileName: lastSentImageRef.current.fileName,
                mimeType: lastSentImageRef.current.mimeType,
              },
            ];
          }
        }
      }

      // Avoid unnecessary state re-renders if messages haven't changed
      setMessages((prev) => {
        if (
          prev.length === allMessages.length &&
          prev[prev.length - 1]?._id === allMessages[allMessages.length - 1]?._id &&
          prev[prev.length - 1]?.messageText === allMessages[allMessages.length - 1]?.messageText
        ) {
          return prev;
        }
        return allMessages;
      });
    } catch (err) {
      console.error("Error fetching messages:", err);
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  // 🔹 2. Initial Load + Polling Setup for current active ticket
  useEffect(() => {
    isInitialLoadRef.current = true;
    fetchMessages(); // Initial load

    // Poll every 6 seconds to simulate real-time without overwhelming network
    const intervalId = setInterval(() => {
      fetchMessages(true); // true = run quietly in background
    }, 6000);

    return () => clearInterval(intervalId); // Cleanup on close or ticket change
  }, [currentTicket._id]);

  // 🔹 3. Fetch user history - Filter ONLY for the person being responded to
  useEffect(() => {
    const fetchUserQueries = async () => {
      try {
        const res = await apiClient.helpdeskApi.get(`/api/admin/tickets`, {
          params: { raisedBy: currentTicket.raisedBy },
        });
        const allTickets: ITicket[] = res.data.data || res.data || [];

        // Filter tickets strictly raised by this specific user
        const userOnly = allTickets.filter((q) => {
          if (currentTicket.raisedBy && q.raisedBy) {
            return q.raisedBy === currentTicket.raisedBy;
          }
          if (currentTicket.parentName && q.parentName) {
            return q.parentName === currentTicket.parentName;
          }
          return false;
        });

        setUserQueries(userOnly.length > 0 ? userOnly : [currentTicket]);
      } catch (err) {
        console.error("Error fetching user queries:", err);
        setUserQueries([currentTicket]);
      }
    };
    fetchUserQueries();
  }, [currentTicket.raisedBy, currentTicket.parentName]);

  // 🔹 4. Smart Auto-Scroll: Only scroll to bottom on initial load, or if user is already near bottom
  const scrollToBottomIfNearBottom = (force = false) => {
    if (!chatRef.current) return;
    const container = chatRef.current;
    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 120;

    if (force || isInitialLoadRef.current || isNearBottom) {
      container.scrollTop = container.scrollHeight;
      isInitialLoadRef.current = false;
    }
  };

  useEffect(() => {
    scrollToBottomIfNearBottom();
  }, [messages]);

  const compressImageToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const maxWidth = 700;
          const maxHeight = 700;
          const scale = Math.min(1, maxWidth / img.width, maxHeight / img.height);
          const canvas = document.createElement("canvas");
          canvas.width = Math.max(1, Math.round(img.width * scale));
          canvas.height = Math.max(1, Math.round(img.height * scale));

          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("Canvas not available"));
            return;
          }

          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          const dataUrl = canvas.toDataURL("image/jpeg", 0.45);
          const base64 = dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;
          resolve(base64);
        };
        img.onerror = () => reject(new Error("Invalid image file"));
        img.src = typeof reader.result === "string" ? reader.result : "";
      };
      reader.onerror = () => reject(new Error("Unable to read image file"));
      reader.readAsDataURL(file);
    });

  const handleFileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showToast("Please select an image file.", "error");
      event.target.value = "";
      return;
    }

    try {
      const base64 = await compressImageToBase64(file);
      setPendingAttachment({
        fileName: file.name,
        mimeType: file.type || "image/jpeg",
        data: base64,
        file,
      });
    } catch (error) {
      console.error("Image compression failed:", error);
      showToast("Image could not be processed. Please try another image.", "error");
    } finally {
      event.target.value = "";
    }
  };

  // 🔹 5. Send Reply with Optimistic Update
  const sendReply = async () => {
    const trimmedReply = reply.trim();
    const hasAttachment = !!pendingAttachment;

    if (!trimmedReply && !hasAttachment) return;

    const messageText = trimmedReply || `📷 Image: ${pendingAttachment?.fileName || "image"}`;
    const payload: Record<string, unknown> = {
      messageText,
      senderId: "admin",
    };

    if (pendingAttachment) {
      payload.imageBase64 = pendingAttachment.data;
      payload.fileName = pendingAttachment.fileName;
    }

    if (pendingAttachment) {
      lastSentImageRef.current = {
        data: pendingAttachment.data,
        fileName: pendingAttachment.fileName,
        mimeType: pendingAttachment.mimeType,
      };
      // Store in persistent cache so image survives server refresh
      const dataUrl = `data:${pendingAttachment.mimeType};base64,${pendingAttachment.data}`;
      imageCache.set(pendingAttachment.fileName, dataUrl);
    } else {
      lastSentImageRef.current = null;
    }

    setReply("");
    setPendingAttachment(null);

    // Optimistic Update: Add to UI immediately
    const tempMsg: IMessage = {
      _id: Date.now().toString(),
      senderId: "admin",
      messageText: messageText,
      sentOn: new Date().toISOString(),
      attachments: pendingAttachment ? [{
        type: "image",
        data: pendingAttachment.data,
        fileName: pendingAttachment.fileName,
        mimeType: pendingAttachment.mimeType,
      }] : undefined,
    };
    setMessages((prev) => [...prev, tempMsg]);
    setTimeout(() => scrollToBottomIfNearBottom(true), 50);

    try {
      // Send to backend
      await apiClient.helpdeskApi.post(
        `/api/admin/tickets/${currentTicket._id}/messages`,
        payload
      );

      // Update status if needed
      if (currentTicket.status !== "In-progress") {
        await apiClient.helpdeskApi.put(`/api/admin/tickets/${currentTicket._id}`, {
          status: "In-progress",
        });
        setCurrentTicket((prev) => ({ ...prev, status: "In-progress" }));
        onUpdate();
      }

      // Refresh to get true server timestamp/ID
      fetchMessages(true);
      lastSentImageRef.current = null;
    } catch (err: any) {
      showToast(
        `Failed to send message: ${
          err.response?.data?.message || "Unknown error"
        }`,
        'error'
      );
    }
  };

  const handleUpdateStatusClick = () => {
    if (onEditStatus) {
      onEditStatus();
      return;
    }

    onClose();
    onUpdate();
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr || dateStr === "N/A") return "—";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return "—";
    }
  };

  const formatTicketStatus = (status?: string) => {
    const raw = (status || "").trim();
    if (!raw || raw.toLowerCase() === "open") return "Received";
    return raw;
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes || bytes <= 0) return "0 KB";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const toImageSrc = (data?: string, mimeType?: string) => {
    if (!data) return undefined;
    const trimmed = data.trim();
    if (!trimmed) return undefined;
    if (trimmed.startsWith("data:image/") || trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      return trimmed;
    }
    return `data:${mimeType || "image/jpeg"};base64,${trimmed}`;
  };

  const getMessageImage = (msg: IMessage) => {
    const attachmentCandidates = [
      ...(msg.attachments || []),
      ...((msg as any).attachment ? [(msg as any).attachment] : []),
      ...(Array.isArray((msg as any).media) ? (msg as any).media : (msg as any).media ? [(msg as any).media] : []),
      ...((msg as any).image ? [(msg as any).image] : []),
      ...((msg as any).file ? [(msg as any).file] : []),
    ];

    const attachment = attachmentCandidates.find((item: any) => {
      const type = item?.type || item?.mimeType || item?.kind || "";
      const data = item?.data || item?.base64 || item?.fileData || item?.image || item?.url;
      return type.includes("image") || !!data;
    });

    const imageData = attachment?.data || attachment?.base64 || attachment?.fileData || attachment?.image || attachment?.url;
    const mimeType = attachment?.mimeType || attachment?.type || "image/jpeg";

    if (imageData) return toImageSrc(imageData, mimeType);

    // Fallback: look up local cache by filename from messageText pattern
    const imagePattern = /^(📷\s*)?Image:\s*(.+)$/i;
    const fileNameMatch = msg.messageText?.trim().match(imagePattern);
    if (fileNameMatch?.[2]) {
      const cached = imageCache.get(fileNameMatch[2].trim());
      if (cached) return cached;
    }

    return undefined;
  };

  const normalizedIssueType = currentTicket.issueType?.trim().toLowerCase() || "";
  const isPaymentOrRefundIssue =
    normalizedIssueType === "payment & refund" ||
    normalizedIssueType === "payment and refund" ||
    (normalizedIssueType.includes("payment") && normalizedIssueType.includes("refund"));
  const isRescheduleIssue = normalizedIssueType === "reschedule screening";
  const showPaymentActions = isPaymentOrRefundIssue || isRescheduleIssue;

  return (
    <div className="response-overlay" onClick={onClose}>
      <div
        className="response-modal wide-popup"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="response-header">
          <h2>Respond to User Query</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="response-body">
          {/* 🔹 LEFT SIDE: Ticket Info & Chat Conversation */}
          <div className="response-left">
            <div className="ticket-details-box">
              <div className="ticket-details-grid">
                {/* Row 1 */}
                <div className="detail-item">
                  <span className="detail-label">Raised By</span>
                  <span className="detail-val strong">{currentTicket.parentName || "—"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Child Name</span>
                  <span className="detail-val strong">{currentTicket.childName || "—"}</span>
                </div>
                <div className="detail-item empty-item"></div>

                {/* Row 2 */}
                <div className="detail-item">
                  <span className="detail-label">Center</span>
                  <span className="detail-val strong">
                    {currentTicket.centerName || (currentTicket as any).center || (currentTicket as any).venueName || currentTicket.issueType || "—"}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Amount Paid</span>
                  <span className="detail-val strong">
                    {(currentTicket as any).amountPaid ?? (currentTicket as any).amount ?? (currentTicket as any).finalAmount ?? "—"}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Payment ID</span>
                  <span className="detail-val strong">
                    {(currentTicket as any).paymentId || (currentTicket as any).payment_id || (currentTicket as any).paymentID || "—"}
                  </span>
                </div>

                {/* Row 3 */}
                <div className="detail-item">
                  <span className="detail-label">Payment Date</span>
                  <span className="detail-val strong">
                    {formatDate((currentTicket as any).paymentDate || currentTicket.createdAt)}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Screening Date</span>
                  <span className="detail-val strong">
                    {formatDate(currentTicket.appointmentDate || (currentTicket as any).screeningDate)}
                  </span>
                </div>
                <div className="detail-item empty-item"></div>

                {/* Row 4 */}
                <div className="detail-item">
                  <span className="detail-label">Issue Type</span>
                  <span className="detail-val strong">{currentTicket.issueType || "—"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <span className="detail-val strong">{formatTicketStatus(currentTicket.status)}</span>
                </div>
                <div className="detail-item empty-item"></div>

                {/* Row 5 */}
                <div className="detail-item">
                  <span className="detail-label">Raised On</span>
                  <span className="detail-val strong">{formatDate(currentTicket.createdAt)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Last Change</span>
                  <span className="detail-val strong">{formatDate(currentTicket.updatedAt)}</span>
                </div>
                <div className="detail-item empty-item"></div>

                {/* Row 6 */}
                <div className="detail-item">
                  <span className="detail-label">Ticket Age (Days)</span>
                  <span className="detail-val strong">{currentTicket.ageing ?? 0}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Status Age (Days)</span>
                  <span className="detail-val strong">{(currentTicket as any).statusAgeing ?? currentTicket.ageing ?? 0}</span>
                </div>
                <div className="detail-item actions-cell">
                  {currentTicket.status?.toLowerCase() !== "closed" && (
                    <div className="ticket-actions-group">
                      <button className="btn-action-update" onClick={handleUpdateStatusClick}>
                        Update
                      </button>
                      {showPaymentActions && (
                        <>
                          <button className="btn-action-reschedule" onClick={() => setIsRescheduleModalOpen(true)}>
                            Reschedule
                          </button>
                          <button className="btn-action-refund" onClick={() => setIsRefundModalOpen(true)}>
                            Refund
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="chat-area" ref={chatRef}>
              {loading ? (
                <Loader text="Loading chat..." />
              ) : (
                messages.map((msg) => {
                  const imageSrc = getMessageImage(msg);

                  // Detect messages that are image-only (no real text body)
                  const imagePattern = /^(📷\s*)?Image:\s*(.+)$/i;
                  const isImageOnlyMsg = imagePattern.test(msg.messageText?.trim() || "");
                  const imageFileName = msg.messageText?.trim().match(imagePattern)?.[2] || "";

                  return (
                    <div
                      key={msg._id}
                      className={`chat-bubble-container ${
                        msg.senderId === "admin" ? "admin" : "user"
                      }`}
                    >
                      <div className="chat-bubble">
                        <p className="sender-info">
                          <strong>
                            {msg.senderId === "admin" ? "Admin" : currentTicket.parentName}
                          </strong>
                          <span>
                            {new Date(msg.sentOn).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </p>

                        {/* Case 1: base64 image data available — render actual image */}
                        {imageSrc && (
                          <img
                            className="chat-message-image"
                            src={imageSrc}
                            alt={imageFileName || "Chat attachment"}
                            onClick={() => setSelectedImage(imageSrc)}
                            style={{ cursor: "pointer" }}
                          />
                        )}

                        {/* Case 2: image-only message but no attachment data (server response) */}
                        {!imageSrc && isImageOnlyMsg && (
                          <div className="chat-image-chip">
                            <span className="chat-image-chip-icon">🖼️</span>
                            <span className="chat-image-chip-name">{imageFileName}</span>
                          </div>
                        )}

                        {/* Case 3: plain text message (show only if NOT a pure image-name message) */}
                        {msg.messageText && !isImageOnlyMsg && (
                          <p className="message-text">{msg.messageText}</p>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {currentTicket.status?.toLowerCase() === "closed" ? (
              <div className="closed-notice-banner">
                <span>🔒 This query is closed and read-only.</span>
              </div>
            ) : (
              <div className="reply-area">
                <div className="reply-composer">
                  <div className="reply-input-wrap">
                    <input
                      type="text"
                      placeholder="Reply to message here"
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && sendReply()}
                    />
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleFileSelection}
                    />
                    <button
                      type="button"
                      className="camera-button"
                      onClick={() => fileInputRef.current?.click()}
                      aria-label="Attach image"
                      title="Attach image"
                    >
                      <span className="camera-icon">📷</span>
                      {/* <span className="camera-label">Image</span> */}
                    </button>
                  </div>

                  {pendingAttachment && (
                    <div className="pending-attachment">
                      <img
                        src={toImageSrc(pendingAttachment.data, pendingAttachment.mimeType)}
                        alt={pendingAttachment.fileName}
                        className="pending-attachment-preview"
                      />
                      <div className="pending-attachment-meta">
                        <span className="pending-attachment-name">{pendingAttachment.fileName}</span>
                        <span className="pending-attachment-size">{formatFileSize(pendingAttachment.file.size)}</span>
                      </div>
                      <button type="button" onClick={() => setPendingAttachment(null)}>Remove</button>
                    </div>
                  )}
                </div>

                <button className="send-button" onClick={sendReply}>Send</button>
              </div>
            )}
          </div>

          {/* 🔹 RIGHT SIDE: User Queries for the person being responded to */}
          <div className="response-right">
            <h2>User Queries ({currentTicket.parentName || "User"})</h2>
            <div className="user-queries-table">
              <table>
                <thead>
                  <tr>
                    <th>Query ID</th>
                    <th>Issue Type</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {userQueries.length > 0 ? (
                    userQueries.map((q) => {
                      const isOriginal = q._id === ticket._id;
                      const isActive = q._id === currentTicket._id;

                      let rowClass = "query-row-item";
                      if (isOriginal && isActive) {
                        rowClass += " original-active-query-row";
                      } else if (isOriginal) {
                        rowClass += " original-query-row";
                      } else if (isActive) {
                        rowClass += " active-query-row";
                      }

                      return (
                        <tr
                          key={q._id}
                          className={rowClass}
                          onClick={() => setCurrentTicket(q)}
                          title={isOriginal ? "Originally opened query" : "Click to view/respond to this query"}
                          style={{ cursor: "pointer" }}
                        >
                          <td>
                            {q._id}
                            {isOriginal && <span className="original-badge"></span>}
                          </td>
                          <td>{q.issueType}</td>
                          <td>
                            {(() => {
                              const displayStatus = formatTicketStatus(q.status);
                              return (
                                <span
                                  className={`status-pill ${displayStatus
                                    .toLowerCase()
                                    .replace(/\s+/g, "-")}`}
                                >
                                  {displayStatus}
                                </span>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={3}>No other queries found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <span>Showing {userQueries.length} queries</span>
            </div>
          </div>
        </div>
      </div>

      {selectedImage && (
        <div className="image-lightbox-overlay" onClick={() => setSelectedImage(null)}>
          <div className="image-lightbox" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="image-lightbox-close"
              onClick={() => setSelectedImage(null)}
              aria-label="Close image"
            >
              ×
            </button>
            <img src={selectedImage} alt="Enlarged chat attachment" className="image-lightbox-img" />
          </div>
        </div>
      )}

      {isRefundModalOpen && (
        <CreateRefundModal
          ticket={currentTicket}
          onClose={() => setIsRefundModalOpen(false)}
          onSuccess={() => {
            fetchMessages(true);
            onUpdate();
          }}
        />
      )}

      {isRescheduleModalOpen && (
        <RescheduleConfigModal
          ticket={currentTicket}
          onClose={() => setIsRescheduleModalOpen(false)}
          onSuccess={() => {
            fetchMessages(true);
            onUpdate();
          }}
        />
      )}
    </div>
  );
};

export default QueryResponseModal;
